import useLoginState from "@shared-composables/useLoginState";
import { createRouter, createWebHistory, NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import useAuthStore from "@shared-stores/auth.store";

const onlyAdminGuard = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) =>
{
	const { isLoggedIn } = useLoginState();

	if (! isLoggedIn.value)
	{
		const authStore = useAuthStore();
		await authStore.init();

		if (authStore.isValidToken())
		{
			next();
		}
		else
		{
			authStore.referer = to?.fullPath;
			next("/login");
		}
	}
	else
	{
		next();
	}
};

const MOVEMENT_DETAIL_REGEX = /^\/movements\/movement\/([0-9A-HJKMNP-TV-Z]{26})(\/.*)?$/;

// @ts-ignore
const router = createRouter({
	// @ts-ignore
	history: createWebHistory(import.meta.env.BASE_URL),
	scrollBehavior: (to, from, savedPosition) =>
	{
		if (savedPosition !== null)
		{
			return new Promise((resolve) =>
			{
				setTimeout(() =>
				{
					resolve({
						left: savedPosition?.left || 0,
						top: savedPosition?.top - 80 || 0,
						behavior: "smooth",
					});
				}, 150);
			});
		}

		return {
			left: 0,
			top: 0,
			behavior: "smooth",
		};
	},
	routes: [
		{
			path: "",
			component: () => import("@/pages/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/login",
			component: () => import("@/pages/login.vue"),
		},

		// API Monitor
		{
			path: "/api-monitor",
			component: () => import("@/pages/api-monitor.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Activities
		{
			path: "/activities",
			component: () => import("@/pages/activities.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Contact Messages
		{
			path: "/box/contact-messages",
			name: "contact-messages",
			component: () => import("@/pages/box/contact-messages/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/contact-messages/:id([0-9A-HJKMNP-TV-Z]{26})",
			name: "contact-message-id",
			component: () => import("@/pages/box/contact-messages/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/contact-messages/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "contact-message-id-actions",
			component: () => import("@/pages/box/contact-messages/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Ventes
		{
			path: "/sales",
			name: "sales",
			component: () => import("@/pages/sales/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sales/shop",
			name: "sales-shop",
			component: () => import("@/pages/sales/shop.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sales/shop/add",
			name: "sales-shop-add",
			component: () => import("@/pages/sales/shop/add.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sales/subscriptions",
			name: "sales-subscriptions",
			component: () => import("@/pages/sales/subscriptions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sales/drop-in",
			name: "sales-drop-in",
			component: () => import("@/pages/sales/drop-in.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sales/events",
			name: "sales-events",
			component: () => import("@/pages/sales/events.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Coupons
		{
			path: "/coupons",
			component: () => import("@/pages/coupons/index.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Communication
		{
			path: "/communication/broadcasts",
			name: "broadcasts",
			component: () => import("@/pages/communication/broadcasts/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/communication/pushes",
			name: "pushes",
			component: () => import("@/pages/communication/pushes/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/communication/emails",
			name: "emails",
			component: () => import("@/pages/communication/emails/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/communication/emails/types",
			name: "email-types",
			component: () => import("@/pages/communication/emails/types.vue"),
			beforeEnter: onlyAdminGuard
		},

		{
			path: "/communication/pushes/tokens",
			name: "fcm-tokens",
			component: () => import("@/pages/communication/pushes/tokens/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/communication/pushes/tokens/:id([0-9A-HJKMNP-TV-Z]{26})/add",
			name: "push-notification-token-add",
			component: () => import("@/pages/communication/pushes/tokens/[id]/add.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/medias",
			name: "medias",
			component: () => import("@/pages/medias/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		// Places
		{
			path: "/places",
			name: "places",
			component: () => import("@/pages/places/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/places/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "place-id",
			component: () => import("@/pages/places/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/places/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "place-id-actions",
			component: () => import("@/pages/places/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		// Parcours (PlaceRoute)
		{
			path: "/places/routes",
			name: "place-routes",
			component: () => import("@/pages/places/routes/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/places/routes/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "place-route-id",
			component: () => import("@/pages/places/routes/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/places/routes/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "place-route-id-actions",
			component: () => import("@/pages/places/routes/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Sondages
		{
			path: "/box/surveys",
			name: "surveys",
			component: () => import("@/pages/box/surveys/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "survey-id",
			component: () => import("@/pages/box/surveys/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id([0-9A-HJKMNP-TV-Z]{26})/questions",
			name: "survey-id-questions",
			component: () => import("@/pages/box/surveys/[id]/questions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id([0-9A-HJKMNP-TV-Z]{26})/preview",
			name: "survey-id-preview",
			component: () => import("@/pages/box/surveys/[id]/preview.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "survey-id-push",
			component: () => import("@/pages/box/surveys/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id([0-9A-HJKMNP-TV-Z]{26})/result",
			name: "survey-id-result",
			component: () => import("@/pages/box/surveys/[id]/result.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/surveys/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "survey-id-actions",
			component: () => import("@/pages/box/surveys/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Objets trouvés
		{
			path: "/box/lost-items",
			name: "lost-items",
			component: () => import("@/pages/box/lost-items/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/lost-items/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "lost-item-id",
			component: () => import("@/pages/box/lost-items/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/lost-items/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "lost-item-id-push",
			component: () => import("@/pages/box/lost-items/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/lost-items/:id([0-9A-HJKMNP-TV-Z]{26})/claims",
			name: "lost-item-id-claims",
			component: () => import("@/pages/box/lost-items/[id]/claims.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/lost-items/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "lost-item-id-actions",
			component: () => import("@/pages/box/lost-items/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/lost-items/:id([0-9A-HJKMNP-TV-Z]{26})/activity",
			name: "lost-item-id-activity",
			component: () => import("@/pages/box/lost-items/[id]/activity.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Planning
		{
			path: "/planning",
			name: "planning",
			component: () => import("@/pages/planning/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/planning/resume",
			name: "planning-resume",
			component: () => import("@/pages/planning/resume.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/planning/attendees",
			name: "planning-attendees",
			component: () => import("@/pages/planning/attendees.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/planning/medias",
			name: "planning-medias",
			component: () => import("@/pages/planning/medias.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Sessions
		{
			path: "/sessions/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "session-id",
			component: () => import("@/pages/sessions/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/workout",
			name: "session-id-workout",
			component: () => import("@/pages/sessions/[id]/workout.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/attendees",
			name: "session-id-attendees",
			component: () => import("@/pages/sessions/[id]/attendees.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/medias",
			name: "session-id-medias",
			component: () => import("@/pages/sessions/[id]/medias.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/activities",
			name: "session-id-activities",
			component: () => import("@/pages/sessions/[id]/activities.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "session-id-push",
			component: () => import("@/pages/sessions/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/sessions/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "session-id-actions",
			component: () => import("@/pages/sessions/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Subscriptions
		{
			path: "/subscriptions",
			name: "subscriptions",
			component: () => import("@/pages/subscriptions/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/preview",
			name: "subscriptions-preview",
			component: () => import("@/pages/subscriptions/preview.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "subscription-id",
			component: () => import("@/pages/subscriptions/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/state",
			name: "subscription-id-state",
			component: () => import("@/pages/subscriptions/[id]/state.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/prices",
			name: "subscription-id-prices",
			component: () => import("@/pages/subscriptions/[id]/prices.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/allowed-workout-types",
			name: "subscription-id-allowed-workout-types",
			component: () => import("@/pages/subscriptions/[id]/allowed-workout-types.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/customers",
			name: "subscription-id-customers",
			component: () => import("@/pages/subscriptions/[id]/customers.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/subscriptions/categories/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			component: () => import("@/pages/subscriptions/categories/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},

		// User Subscriptions
		{
			path: "/user-subscriptions",
			name: "user-subscriptions",
			component: () => import("@/pages/user-subscriptions/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "user-subscription-id",
			component: () => import("@/pages/user-subscriptions/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/sessions",
			name: "user-subscription-id-sessions",
			component: () => import("@/pages/user-subscriptions/[id]/sessions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/workout-types",
			name: "user-subscription-id-workout-types",
			component: () => import("@/pages/user-subscriptions/[id]/workout-types.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/invoices",
			name: "user-subscription-id-invoices",
			component: () => import("@/pages/user-subscriptions/[id]/invoices.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "user-subscription-id-actions",
			component: () => import("@/pages/user-subscriptions/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/user-subscriptions/:id([0-9A-HJKMNP-TV-Z]{26})/delete",
			name: "user-subscription-id-delete",
			component: () => import("@/pages/user-subscriptions/[id]/delete.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Users
		{
			path: "/users",
			name: "users",
			component: () => import("@/pages/users/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "user-id",
			component: () => import("@/pages/users/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/subscriptions",
			name: "user-id-subscriptions",
			component: () => import("@/pages/users/[id]/subscriptions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/sessions",
			name: "user-id-sessions",
			component: () => import("@/pages/users/[id]/sessions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "user-id-push",
			component: () => import("@/pages/users/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/activities",
			name: "user-id-activities",
			component: () => import("@/pages/users/[id]/activities.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/events",
			name: "user-id-events",
			component: () => import("@/pages/users/[id]/events.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/surveys",
			name: "user-id-surveys",
			component: () => import("@/pages/users/[id]/surveys.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/lost-items",
			name: "user-id-lost-items",
			component: () => import("@/pages/users/[id]/lost-items.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/users/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "user-id-actions",
			component: () => import("@/pages/users/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Workout Type
		{
			path: "/workout-types",
			name: "workout-types",
			component: () => import("@/pages/workout-types/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-types/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "workout-type-id",
			component: () => import("@/pages/workout-types/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-types/:id([0-9A-HJKMNP-TV-Z]{26})/sessions",
			name: "workout-type-id-sessions",
			component: () => import("@/pages/workout-types/[id]/sessions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-types/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "workout-type-id-actions",
			component: () => import("@/pages/workout-types/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Event Type
		{
			path: "/event-types",
			name: "event-types",
			component: () => import("@/pages/event-types/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/event-types/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "event-type-id",
			component: () => import("@/pages/event-types/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/event-types/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "event-type-id-actions",
			component: () => import("@/pages/event-types/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Actualités de la box
		{
			path: "/box/news",
			name: "news",
			component: () => import("@/pages/box/news/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/news/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "news-id",
			component: () => import("@/pages/box/news/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/news/:id([0-9A-HJKMNP-TV-Z]{26})/preview",
			name: "news-id-preview",
			component: () => import("@/pages/box/news/[id]/preview.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/news/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "news-id-push",
			component: () => import("@/pages/box/news/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/news/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "news-id-actions",
			component: () => import("@/pages/box/news/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Event Tag
		{
			path: "/event-tags",
			name: "event-tags",
			component: () => import("@/pages/event-tags/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/event-tags/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "event-tag-id",
			component: () => import("@/pages/event-tags/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/event-tags/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "event-tag-id-actions",
			component: () => import("@/pages/event-tags/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Event
		{
			path: "/events",
			name: "events",
			component: () => import("@/pages/events/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "event-id",
			component: () => import("@/pages/events/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id([0-9A-HJKMNP-TV-Z]{26})/participants",
			name: "event-id-participants",
			component: () => import("@/pages/events/[id]/participants.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id([0-9A-HJKMNP-TV-Z]{26})/push",
			name: "event-id-push",
			component: () => import("@/pages/events/[id]/push.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id([0-9A-HJKMNP-TV-Z]{26})/email",
			name: "event-id-email",
			component: () => import("@/pages/events/[id]/email.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id([0-9A-HJKMNP-TV-Z]{26})/activities",
			name: "event-id-activities",
			component: () => import("@/pages/events/[id]/activities.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/events/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "event-id-actions",
			component: () => import("@/pages/events/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Equipment (Box → Équipement)
		{
			path: "/box/equipment",
			name: "equipment",
			component: () => import("@/pages/box/equipment/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/equipment/items/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "equipment-item-id",
			component: () => import("@/pages/box/equipment/items/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/equipment/items/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "equipment-item-id-actions",
			component: () => import("@/pages/box/equipment/items/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/equipment/types/:id(-1|[0-9A-HJKMNP-TV-Z]{26})",
			name: "equipment-type-id",
			component: () => import("@/pages/box/equipment/types/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/box/equipment/types/:id([0-9A-HJKMNP-TV-Z]{26})/actions",
			name: "equipment-type-id-actions",
			component: () => import("@/pages/box/equipment/types/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Anatomie (Contenu → BodyPart + MuscleGroup unifiés)
		{
			path: "/anatomy",
			name: "anatomy",
			component: () => import("@/pages/anatomy/index.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Bibliothèque mouvements : catégories + CRUD Movement
		{
			path: "/movements",
			name: "movements",
			component: () => import("@/pages/movements/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/muscles",
			name: "movements-muscles",
			component: () => import("@/pages/movements/muscles/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movement-families",
			name: "movement-families",
			component: () => import("@/pages/movement-families/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movement-families/:id",
			name: "movement-family",
			component: () => import("@/pages/movement-families/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movement-families/:id/actions",
			name: "movement-family-actions",
			component: () => import("@/pages/movement-families/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-tags",
			name: "workout-tags",
			component: () => import("@/pages/workout-tags/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-tags/:id",
			name: "workout-tag",
			component: () => import("@/pages/workout-tags/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-tags/:id/actions",
			name: "workout-tag-actions",
			component: () => import("@/pages/workout-tags/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/products",
			name: "products",
			component: () => import("@/pages/products/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/products/:id",
			name: "product",
			component: () => import("@/pages/products/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/products/:id/stock",
			name: "product-stock",
			component: () => import("@/pages/products/[id]/stock.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/products/:id/documents",
			name: "product-documents",
			component: () => import("@/pages/products/[id]/documents.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/products/:id/actions",
			name: "product-actions",
			component: () => import("@/pages/products/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stock",
			name: "stock",
			component: () => import("@/pages/stock/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stock/movements",
			name: "stock-movements",
			component: () => import("@/pages/stock/movements.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stock/movements/incoming",
			name: "stock-movements-incoming",
			component: () => import("@/pages/stock/incoming.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stock/expirations",
			name: "stock-expirations",
			component: () => import("@/pages/stock/expirations.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/suppliers",
			name: "suppliers",
			component: () => import("@/pages/suppliers/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/suppliers/:id",
			name: "supplier",
			component: () => import("@/pages/suppliers/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/suppliers/:id/actions",
			name: "supplier-actions",
			component: () => import("@/pages/suppliers/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-models",
			name: "product-models",
			component: () => import("@/pages/product-models/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-models/:id",
			name: "product-model",
			component: () => import("@/pages/product-models/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-models/:id/actions",
			name: "product-model-actions",
			component: () => import("@/pages/product-models/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-categories",
			name: "product-categories",
			component: () => import("@/pages/product-categories/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-categories/:id",
			name: "product-category",
			component: () => import("@/pages/product-categories/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/product-categories/:id/actions",
			name: "product-category-actions",
			component: () => import("@/pages/product-categories/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-levels",
			name: "workout-levels",
			component: () => import("@/pages/workout-levels/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-levels/:id",
			name: "workout-level",
			component: () => import("@/pages/workout-levels/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-levels/:id/actions",
			name: "workout-level-actions",
			component: () => import("@/pages/workout-levels/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-block-types",
			name: "workout-block-types",
			component: () => import("@/pages/workout-block-types/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-block-types/:id",
			name: "workout-block-type",
			component: () => import("@/pages/workout-block-types/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-block-types/:id/actions",
			name: "workout-block-type-actions",
			component: () => import("@/pages/workout-block-types/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-templates",
			name: "workout-templates",
			component: () => import("@/pages/workout-templates/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-templates/:id",
			name: "workout-template",
			component: () => import("@/pages/workout-templates/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workout-templates/:id/actions",
			name: "workout-template-actions",
			component: () => import("@/pages/workout-templates/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workouts",
			name: "workouts",
			component: () => import("@/pages/workouts/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workouts/:id",
			name: "workout",
			component: () => import("@/pages/workouts/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/workouts/:id/actions",
			name: "workout-actions",
			component: () => import("@/pages/workouts/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/category/:id",
			name: "movement-category",
			component: () => import("@/pages/movements/category/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/category/:id/actions",
			name: "movement-category-actions",
			component: () => import("@/pages/movements/category/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/movement/:id",
			name: "movement",
			component: () => import("@/pages/movements/movement/[id]/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/movement/:id/guide",
			name: "movement-guide",
			component: () => import("@/pages/movements/movement/[id]/guide.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/movement/:id/records",
			name: "movement-records",
			component: () => import("@/pages/movements/movement/[id]/records.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/movements/movement/:id/actions",
			name: "movement-actions",
			component: () => import("@/pages/movements/movement/[id]/actions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Paramètres
		{
			path: "/box/settings",
			component: () => import("@/pages/box/settings/index.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Stats
		{
			path: "/stats",
			name: "stats",
			component: () => import("@/pages/stats/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stats/customers",
			name: "stats-customers",
			component: () => import("@/pages/stats/customers.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/stats/sessions",
			name: "stats-sessions",
			component: () => import("@/pages/stats/sessions.vue"),
			beforeEnter: onlyAdminGuard
		},

		// Documentation
		{
			path: "/documentation",
			name: "documentation",
			component: () => import("@/pages/documentation/index.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/legislation",
			name: "documentation-legislation",
			component: () => import("@/pages/documentation/legislation.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/couverture",
			name: "documentation-couverture",
			component: () => import("@/pages/documentation/couverture.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/events",
			name: "documentation-events",
			component: () => import("@/pages/documentation/events.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/emails",
			name: "documentation-emails",
			component: () => import("@/pages/documentation/emails.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/subscriptions",
			name: "documentation-shop-subscriptions",
			component: () => import("@/pages/documentation/shop/subscriptions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/subscriptions-e2e",
			name: "documentation-shop-subscriptions-e2e",
			component: () => import("@/pages/documentation/shop/subscriptions-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/drop-ins",
			name: "documentation-shop-drop-ins",
			component: () => import("@/pages/documentation/shop/drop-ins.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/drop-ins-e2e",
			name: "documentation-shop-drop-ins-e2e",
			component: () => import("@/pages/documentation/shop/drop-ins-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/stripe",
			name: "documentation-stripe",
			component: () => import("@/pages/documentation/stripe.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/deployment",
			name: "documentation-deployment",
			component: () => import("@/pages/documentation/deployment.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/planning/sessions",
			name: "documentation-planning-sessions",
			component: () => import("@/pages/documentation/planning/sessions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/planning/sessions-e2e",
			name: "documentation-planning-sessions-e2e",
			component: () => import("@/pages/documentation/planning/sessions-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/planning/places",
			name: "documentation-planning-places",
			component: () => import("@/pages/documentation/planning/places.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/planning/places-e2e",
			name: "documentation-planning-places-e2e",
			component: () => import("@/pages/documentation/planning/places-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movement-categories",
			name: "documentation-movement-movement-categories",
			component: () => import("@/pages/documentation/movement/movement-categories.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movement-categories-e2e",
			name: "documentation-movement-movement-categories-e2e",
			component: () => import("@/pages/documentation/movement/movement-categories-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movements",
			name: "documentation-movement-movements",
			component: () => import("@/pages/documentation/movement/movements.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movements-e2e",
			name: "documentation-movement-movements-e2e",
			component: () => import("@/pages/documentation/movement/movements-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movement-relations",
			name: "documentation-movement-movement-relations",
			component: () => import("@/pages/documentation/movement/movement-relations.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/movement-relations-e2e",
			name: "documentation-movement-movement-relations-e2e",
			component: () => import("@/pages/documentation/movement/movement-relations-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/workouts",
			name: "documentation-workout-workouts",
			component: () => import("@/pages/documentation/workout/workouts.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/workouts-e2e",
			name: "documentation-workout-workouts-e2e",
			component: () => import("@/pages/documentation/workout/workouts-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/workouts/user-scores",
			name: "documentation-workout-user-scores",
			component: () => import("@/pages/documentation/workout/user-scores.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/workouts/user-scores-e2e",
			name: "documentation-workout-user-scores-e2e",
			component: () => import("@/pages/documentation/workout/user-scores-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/e2e",
			name: "documentation-profile-e2e",
			component: () => import("@/pages/documentation/profile/e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/cancel-reactivate",
			name: "documentation-profile-cancel-reactivate",
			component: () => import("@/pages/documentation/profile/cancel-reactivate.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/cancel-reactivate-e2e",
			name: "documentation-profile-cancel-reactivate-e2e",
			component: () => import("@/pages/documentation/profile/cancel-reactivate-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/change-rollback",
			name: "documentation-profile-change-rollback",
			component: () => import("@/pages/documentation/profile/change-rollback.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/change-rollback-e2e",
			name: "documentation-profile-change-rollback-e2e",
			component: () => import("@/pages/documentation/profile/change-rollback-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/pause-resume",
			name: "documentation-profile-pause-resume",
			component: () => import("@/pages/documentation/profile/pause-resume.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/profile/pause-resume-e2e",
			name: "documentation-profile-pause-resume-e2e",
			component: () => import("@/pages/documentation/profile/pause-resume-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/user-subscriptions",
			name: "documentation-shop-user-subscriptions",
			component: () => import("@/pages/documentation/shop/user-subscriptions.vue"),
			beforeEnter: onlyAdminGuard
		},
		{
			path: "/documentation/shop/user-subscriptions-e2e",
			name: "documentation-shop-user-subscriptions-e2e",
			component: () => import("@/pages/documentation/shop/user-subscriptions-e2e.vue"),
			beforeEnter: onlyAdminGuard
		},
	]
});

router.afterEach((to, from) =>
{
	if (to.path === "/movements")
	{
		const match = from.path.match(MOVEMENT_DETAIL_REGEX);

		if (match)
		{
			sessionStorage.setItem("movements-scroll-to-id", match[1]);
		}
	}
});

export default router;
